import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, Edit, Trash2, AlertCircle, Eye, EyeOff, MapPin, Upload, Download } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getCities, deleteCity, updateCity, City, addCity } from '../../services/cityService';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';

const CitiesList: React.FC = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }

    const fetchCities = async () => {
      try {
        setLoading(true);
        const citiesData = await getCities();
        setCities(citiesData);
      } catch (err) {
        setError(t('error'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCities();
  }, [isAdmin, navigate, t]);

  const handleDeleteClick = (cityId: string) => {
    setDeleteConfirm(cityId);
  };

  const handleDeleteConfirm = async (cityId: string) => {
    try {
      await deleteCity(cityId);
      setCities(cities.filter(city => city.id !== cityId));
      setDeleteConfirm(null);
    } catch (err) {
      setError(t('error'));
      console.error(err);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const toggleCityStatus = async (city: City) => {
    try {
      await updateCity(city.id, { active: !city.active });
      setCities(cities.map(c => 
        c.id === city.id ? { ...c, active: !c.active } : c
      ));
    } catch (err) {
      setError(t('error'));
      console.error(err);
    }
  };

  const downloadTemplate = () => {
    const template = [
      ['name', 'state', 'country', 'active'],
      ['New York', 'NY', 'USA', true],
      ['Los Angeles', 'CA', 'USA', true]
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cities Template');
    XLSX.writeFile(wb, 'cities_import_template.xlsx');
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      setError(null);
      setSuccess(null);

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          let imported = 0;
          let errors = 0;

          for (const row of jsonData) {
            try {
              if (!row.name) continue;

              await addCity({
                name: String(row.name),
                state: row.state ? String(row.state) : undefined,
                country: row.country ? String(row.country) : undefined,
                active: row.active === false ? false : true
              });
              imported++;
            } catch (err) {
              console.error(`Error importing city ${row.name}:`, err);
              errors++;
            }
          }

          // Refresh the cities list
          const updatedCities = await getCities();
          setCities(updatedCities);

          setSuccess(`Successfully imported ${imported} cities${errors > 0 ? ` (${errors} errors)` : ''}`);
        } catch (err) {
          setError('Error processing file. Please make sure it follows the template format.');
          console.error('Error processing file:', err);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      setError('Error uploading file');
      console.error('Error uploading file:', err);
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Cities</h1>
        <div className="flex space-x-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={downloadTemplate}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md flex items-center transition-colors duration-300"
            >
              <Download className="h-5 w-5 mr-2" />
              Download Template
            </button>
            <label className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md flex items-center transition-colors duration-300 cursor-pointer">
              <Upload className="h-5 w-5 mr-2" />
              Import Cities
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                disabled={importing}
              />
            </label>
          </div>
          <Link
            to="/admin/cities/new"
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md flex items-center transition-colors duration-300"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            Add New City
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
          <p className="text-green-700">{success}</p>
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Import Instructions</h2>
        <p className="text-gray-600 mb-4">
          To import cities in bulk, please follow these steps:
        </p>
        <ol className="list-decimal list-inside space-y-2 text-gray-600">
          <li>Download the template file using the button above</li>
          <li>Fill in the spreadsheet with your cities data using the following columns:
            <ul className="list-disc list-inside ml-6 mt-2">
              <li><code className="bg-gray-100 px-2 py-1 rounded">name</code> (required): City name</li>
              <li><code className="bg-gray-100 px-2 py-1 rounded">state</code> (optional): State or province</li>
              <li><code className="bg-gray-100 px-2 py-1 rounded">country</code> (optional): Country name</li>
              <li><code className="bg-gray-100 px-2 py-1 rounded">active</code> (optional): true/false (defaults to true)</li>
            </ul>
          </li>
          <li>Save the file and upload it using the "Import Cities" button</li>
        </ol>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">{importing ? 'Importing cities...' : t('loading')}</p>
        </div>
      ) : cities.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <p className="text-gray-500 text-lg mb-4">No cities found.</p>
          <Link
            to="/admin/cities/new"
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md inline-flex items-center transition-colors duration-300"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            Add Your First City
          </Link>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    State
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Country
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cities.map((city) => (
                  <tr key={city.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <MapPin className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">{city.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {city.state || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {city.country || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        city.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {city.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {deleteConfirm === city.id ? (
                        <div className="flex items-center justify-end space-x-2">
                          <span className="text-gray-500 mr-2">Confirm delete?</span>
                          <button
                            onClick={() => handleDeleteConfirm(city.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Yes
                          </button>
                          <button
                            onClick={handleDeleteCancel}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => toggleCityStatus(city)}
                            className={`${city.active ? 'text-gray-600' : 'text-green-600'} hover:text-gray-900`}
                            title={city.active ? 'Deactivate' : 'Activate'}
                          >
                            {city.active ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                          <Link
                            to={`/admin/cities/edit/${city.id}`}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <Edit className="h-5 w-5" />
                          </Link>
                          <button
                            onClick={() => handleDeleteClick(city.id)}
                            className="text-red-600 hover:text-red-900 ml-3"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CitiesList;